import { Row } from '@tanstack/react-table';
import { Account } from '@/features/accounts/types';
import { TableRow } from '@/shared/components/Table';
import { useAccountSchedules } from '@/features/accounts/schedule/hooks/useAccountSchedules';
import { useScheduleActivation } from '@/features/accounts/schedule/activation/hooks/useScheduleActivation';
import { useScheduleManagement } from '@/features/accounts/schedule/management/hooks/useScheduleManagement';
import { useScheduleControls } from '@/features/accounts/schedule/hooks/useScheduleControls';
import ScheduleActivationModals from '@/features/accounts/schedule/activation/components/ScheduleActivationModals';
import ScheduleManagementModals from '@/features/accounts/schedule/management/components/ScheduleManagementModals';
import ScheduleSubRows from './ScheduleSubRows';
import {
  ExpandCell,
  AccountCell,
  StatusCell,
  DateRangeCell,
  CurrentAppointmentCell,
  ActionsCell,
} from './cells';
import DeleteAccountButton from '@/features/accounts/removal/components/DeleteAccountButton';
import { Button as EditPasswordButton } from '@/features/accounts/credentials/components/EditPassword';

interface Props {
  row: Row<Account>;
  onActivated: () => void;
}

export default function AccountRow({ row, onActivated }: Props) {
  const account = row.original;
  const {
    primarySchedule,
    additionalSchedules,
    hasMoreSchedules,
    isLoading,
    error,
  } = useAccountSchedules(account.id);

  const activation = useScheduleActivation({ onActivated });
  const management = useScheduleManagement();
  const actions = useScheduleControls(activation, management);

  return (
    <>
      <TableRow>
        <td className="px-4 py-3">
          <div className="flex items-start gap-2 min-w-0">
            <div className="w-6 shrink-0 pt-0.5">
              <ExpandCell row={row} hasMultipleSchedules={hasMoreSchedules} />
            </div>
            <div className="min-w-0">
              <AccountCell
                account={account}
                schedule={primarySchedule}
                isLoading={isLoading}
                error={error}
              />
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <StatusCell schedule={primarySchedule} />
        </td>
        <td className="px-4 py-3 text-sm text-slate-600">
          <DateRangeCell schedule={primarySchedule} />
        </td>
        <td className="px-4 py-3 text-sm text-slate-600">
          <CurrentAppointmentCell schedule={primarySchedule} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap" style={{ width: '1%' }}>
          <ActionsCell schedule={primarySchedule} actions={actions} />
        </td>
        <td className="px-2 py-3">
          <div className="flex items-center gap-1">
            <EditPasswordButton account={account} />
            <DeleteAccountButton account={account} />
          </div>
        </td>
      </TableRow>
      {row.getIsExpanded() && (
        <ScheduleSubRows schedules={additionalSchedules} actions={actions} />
      )}

      <ScheduleActivationModals countryCode={account.country_code} activation={activation} />
      <ScheduleManagementModals countryCode={account.country_code} management={management} />
    </>
  );
}
